import React, {FC, memo, ReactNode, useReducer, useState} from 'react';
import cx from 'classnames';
import {HTMLDialog, TreeNodeProps} from '@priority-software/priority-style-react';
import dialogCSS from './common-dialog-styles.module.scss';
import {emailDialogID} from './testIDs';
import {emailReducer} from './reducer';
import {useEmailContextAPI} from './hooks/useEmailContextAPI';
import {DialogToolbarContent} from '../../components/Dialog/DialogToolbar/DialogToolbar';
import {EmailContext} from './context';
import {BodyEmailEditor} from './components/BodyEmailEditor';
import {FooterEmailEditor} from './components/FooterEmailEditor';
import {TVoidFunction} from './types';
import {TEditorTemplatesDictionary, TEmailContextType, TEmailReducerType} from './reducer/types';
import {renderDefault} from './utils/common';
import {useHandlePressKey} from './components/RichText/parts/hooks/useHandleKeyPress';
import {createDataTestId} from '../../utils/dataTestId';
import {texts} from './texts';
import {defaultContext} from './reducer/model';
import {TAlertMessageDictionary} from '../Automations/types';

interface IEmailDialogProps {
    isOpen: boolean;
    toolbarContent?: ReactNode;
    onClose?: TVoidFunction;
    initialTemplatesData?: TAlertMessageDictionary;
    treeValues: TreeNodeProps;
    onSave?: (editorState: TEditorTemplatesDictionary) => void;
    subTitle?: string;
    description?: string;
}

export const EmailDialog: FC<IEmailDialogProps> = memo(
    ({isOpen, onClose, initialTemplatesData, treeValues, onSave, subTitle, description}) => {
        const [contextValue, dispatch] = useReducer<TEmailReducerType, TEmailContextType>(
            (state, action) => emailReducer(state, action, initialTemplatesData),
            defaultContext,
            _ => renderDefault(initialTemplatesData, treeValues)
        );

        const contextHandlers = useEmailContextAPI({
            dispatch,
            contextValue,
            onSave
        });

        const [focusSubject, setFocusSubject] = useState(false);

        useHandlePressKey({
            keyTyped: 'keydown',
            keyPressed: 'Escape',
            cb: () => contextHandlers.onBack(onClose)
        });

        if (!isOpen) return null;

        return (
            <EmailContext.Provider
                value={{
                    states: {...contextValue.states, focusSubject},
                    handlers: {...contextHandlers, setFocusSubject}
                }}
            >
                <HTMLDialog
                    {...createDataTestId('email-dialog', true)}
                    id={emailDialogID}
                    escClosable
                    isOpen={isOpen}
                    onClose={() => contextHandlers.onBack(onClose)}
                    componentClasses={cx(
                        dialogCSS.component,
                        dialogCSS.paddingBottom0,
                        'reactComp'
                    )}
                    toolbarContent={
                        <DialogToolbarContent
                            mainTitle={texts.email_editor}
                            subTitle={subTitle}
                            description={description}
                            helpText={texts.email_help_text}
                            tooltipPortalID={emailDialogID}
                        />
                    }
                    content={<BodyEmailEditor />}
                    footer={<FooterEmailEditor {...{onClose, treeValues}} />}
                    size="large"
                />
            </EmailContext.Provider>
        );
    }
);
